const PAC_STAGE_KEYS = new Set([
  'proposal_contract',
  'proposal_quotation',
  'agreement_confirmation',
  'contract_signing',
]);

const PREP_OR_LIVE = new Set(['event_preparation', 'event_live', 'accepted', 'approved']);

export const PROPOSAL_REVIEW_STATUSES = {
  PENDING_CLIENT: 'pending_client',
  PENDING_ADMIN: 'pending_admin',
  APPROVED: 'approved',
};

export const CONTRACT_REVIEW_STATUSES = {
  PENDING_UPLOAD: 'pending_upload',
  PENDING_ADMIN: 'pending_admin',
  NEEDS_REVISION: 'needs_revision',
  ACCEPTED: 'accepted',
};

export const CONTRACT_BUCKET = 'signed-contracts';
export const CONTRACT_TEMPLATE_TYPE = 'contract_template';
export const SIGNED_CONTRACT_TYPE = 'signed_contract';

export const ALLOWED_PROPOSAL_EDIT_FIELDS = [
  'event_name',
  'theme',
  'venue',
  'budget_range',
  'attendee_count',
  'event_type',
  'details',
  'specific_needs',
  'concert_vendor_choices',
];

export const normalizeWorkflowValue = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, '_');

export const isLegacyPacStage = (value) => PAC_STAGE_KEYS.has(normalizeWorkflowValue(value));

export const toClientWorkflowStage = (value) => {
  const normalized = normalizeWorkflowValue(value);
  if (PAC_STAGE_KEYS.has(normalized)) return 'proposal_contract';
  if (normalized === 'approved' || normalized === 'accepted') return 'event_preparation';
  return normalized;
};

export const getAttachments = (booking) => (Array.isArray(booking?.attachments) ? booking.attachments : []);

export const findAttachmentByType = (booking, type) => (
  getAttachments(booking).find((item) => item?.type === type) || null
);

export const hasSignedContract = (booking) => Boolean(
  booking?.signed_contract_path
  || booking?.signed_contract_url
  || findAttachmentByType(booking, SIGNED_CONTRACT_TYPE)
);

export const getContractReviewStatus = (booking) => {
  const explicit = normalizeWorkflowValue(booking?.contract_review_status);
  if (explicit === CONTRACT_REVIEW_STATUSES.ACCEPTED) return CONTRACT_REVIEW_STATUSES.ACCEPTED;
  if (explicit === CONTRACT_REVIEW_STATUSES.NEEDS_REVISION) return CONTRACT_REVIEW_STATUSES.NEEDS_REVISION;
  if (explicit === CONTRACT_REVIEW_STATUSES.PENDING_ADMIN) return CONTRACT_REVIEW_STATUSES.PENDING_ADMIN;
  if (explicit === CONTRACT_REVIEW_STATUSES.PENDING_UPLOAD) return CONTRACT_REVIEW_STATUSES.PENDING_UPLOAD;
  if (hasSignedContract(booking)) return CONTRACT_REVIEW_STATUSES.PENDING_ADMIN;
  return CONTRACT_REVIEW_STATUSES.PENDING_UPLOAD;
};

export const getContractReviewLabel = (booking) => {
  const status = getContractReviewStatus(booking);
  if (status === CONTRACT_REVIEW_STATUSES.ACCEPTED) return 'Contract Accepted';
  if (status === CONTRACT_REVIEW_STATUSES.NEEDS_REVISION) return 'Contract Needs Revision';
  if (status === CONTRACT_REVIEW_STATUSES.PENDING_ADMIN) return 'Contract Resubmitted — Awaiting Admin Review';
  return 'Awaiting signed contract';
};

export const isContractAccepted = (booking) => (
  getContractReviewStatus(booking) === CONTRACT_REVIEW_STATUSES.ACCEPTED
);

export const canClientReplaceSignedContract = (booking) => !isContractAccepted(booking);

export const canAdminRequestContractResubmission = (booking, reason) => (
  hasSignedContract(booking)
  && !isContractAccepted(booking)
  && String(reason || '').trim() !== ''
);

export const canAdminAcceptContract = (booking) => (
  hasSignedContract(booking) && !isContractAccepted(booking)
);

export const isCorporateContractBooking = (booking) => (
  normalizeWorkflowValue(booking?.booking_type) === 'corporate'
);

export const getOfficialContractTitle = (booking) => (
  isCorporateContractBooking(booking)
    ? 'CORPORATE EVENT BOOKING AGREEMENT'
    : 'CONCERT EVENT BOOKING AGREEMENT'
);

export const getStoredContract = (booking) => {
  const signed = findAttachmentByType(booking, SIGNED_CONTRACT_TYPE);
  if (signed?.path || signed?.url) return signed;
  if (booking?.signed_contract_path || booking?.signed_contract_url) {
    return {
      type: SIGNED_CONTRACT_TYPE,
      path: booking.signed_contract_path || '',
      url: booking.signed_contract_url || '',
      name: 'Signed contract',
    };
  }
  return findAttachmentByType(booking, CONTRACT_TEMPLATE_TYPE);
};

export const getContractTemplate = (booking) => findAttachmentByType(booking, CONTRACT_TEMPLATE_TYPE);

export const isPdfFile = (file) => {
  if (!file) return false;
  const type = String(file.type || '').toLowerCase();
  const name = String(file.name || '').toLowerCase();
  return type === 'application/pdf' || name.endsWith('.pdf');
};

export const isProposalDetailsApproved = (booking) => {
  const reviewStatus = normalizeWorkflowValue(booking?.proposal_review_status);
  if (reviewStatus === PROPOSAL_REVIEW_STATUSES.APPROVED) return true;
  if (!reviewStatus && (booking?.client_agreement_confirmed || booking?.agreement_confirmed) && hasSignedContract(booking)) {
    return true;
  }
  return false;
};

export const isProposalContractComplete = (booking) => Boolean(
  (booking?.client_agreement_confirmed || booking?.agreement_confirmed)
  && hasSignedContract(booking)
  && isProposalDetailsApproved(booking)
  && isContractAccepted(booking)
);

export const isProposalContractViewOnly = (booking, currentStage) => (
  (isProposalContractComplete(booking) && ['event_preparation', 'event_live'].includes(normalizeWorkflowValue(currentStage)))
  || isContractAccepted(booking)
);

export const shouldShowProposalContractIcon = (booking, currentStage) => {
  const stage = normalizeWorkflowValue(currentStage);
  if (stage === 'proposal_contract') return true;
  return Boolean(getStoredContract(booking));
};

export const pickProposalEditValues = (source = {}) => ({
  event_name: String(source.event_name || '').trim(),
  theme: String(source.theme || '').trim(),
  venue: String(source.venue || '').trim(),
  budget_range: String(source.budget_range || '').trim(),
  attendee_count: source.attendee_count === '' || source.attendee_count == null ? null : Number(source.attendee_count),
  event_type: String(source.event_type || '').trim(),
  details: String(source.details || '').trim(),
  specific_needs: String(source.specific_needs || '').trim(),
  concert_vendor_choices: source.concert_vendor_choices || {},
});

export const upsertAttachment = (attachments, nextAttachment) => {
  const existing = Array.isArray(attachments) ? attachments : [];
  return [
    ...existing.filter((item) => item?.type !== nextAttachment.type),
    nextAttachment,
  ];
};

export { PAC_STAGE_KEYS, PREP_OR_LIVE };
